// Clinical-task contract types (MR-TASKS-BIRTH C2, OpenSpec
// `monorepo-tasks-data-core`).
//
// Schema types are re-picked from the `@heidi/schema/ml-scribe` door directly —
// severing the deprecated `@/types` barrel pick (`src/types/index.ts` now
// re-exports `ClinicalTask`, `AssignmentFilter`, and `ClinicalTasksQueryParams`
// from here, so each has exactly one definition). The hand-written sort
// vocabulary moved verbatim from `src/types/clinical-tasks.ts`, whose
// presentation half (lucide maps, `DateRange` filter helpers) stays app-side
// behind the split shim.
import { type components } from '@heidi/schema/ml-scribe';

// --- schema re-picks -------------------------------------------------------

// The backend already returns `suggested_prompt`; the generated schema is
// stale. Hand-widened until `pnpm run schema:dev` catches up, and `types.test.ts`
// fails the build when it does — an intersection over a field the schema now
// declares is not a harmless no-op, it would silently re-widen the real type
// (a non-null `string` back to `string | null`) with nothing to say so.
export type ClinicalTask = components['schemas']['ClinicalTaskView'] & {
  suggested_prompt?: string | null;
};
export type ClinicalTaskCategory = components['schemas']['ClinicalTaskCategory'];
export type ClinicalTaskStatus = components['schemas']['ClinicalTaskStatus'];
export type AssigneeType = components['schemas']['AssigneeType'];
export type ClinicalTaskLink = components['schemas']['ClinicalTaskLink'];
export type ClinicalTaskLinkType = components['schemas']['ClinicalTaskLinkType'];
export type ClinicalTaskResponse = components['schemas']['ClinicalTaskResponse'];
export type GetClinicalTasksResponse = components['schemas']['GetClinicalTasksResponse'];
export type CreateClinicalTaskRequest =
  components['schemas']['CreateClinicalTaskRequest'];
export type UpdateClinicalTaskRequest =
  components['schemas']['UpdateClinicalTaskRequest'];

/** A not-yet-persisted task carrying its optimistic placeholder id. */
export interface ClinicalTaskWithTempId extends ClinicalTask {
  tempId?: string;
}

// --- request params --------------------------------------------------------

export enum AssignmentFilter {
  all = 'all',
  created_by_me = 'created_by_me',
  assigned_to_me = 'assigned_to_me',
  assigned_to_team = 'assigned_to_team',
}

export interface ClinicalTasksQueryParams {
  sessionId?: string;
  /**
   * Link filter — returns the tasks attached to one linked resource. Unlike the
   * default listing this is scoped by workspace rather than creator/assignee,
   * so any teammate sees a resource's tasks.
   *
   * One object rather than two optional fields: ml-scribe 422s on a
   * half-supplied pair, so the compiler holds the same invariant the server
   * does and a lone half cannot be written.
   */
  link?: { type: ClinicalTaskLinkType; id: string };
  status?: ClinicalTaskStatus;
  category?: ClinicalTaskCategory;
  searchTerm?: string;
  dateFrom?: Date;
  dateTo?: Date;
  dueDateFrom?: Date;
  dueDateTo?: Date;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  skip?: number;
  limit?: number;
  assignmentFilter?: AssignmentFilter;
}

// --- sort vocabulary (moved verbatim from `src/types/clinical-tasks.ts`) ---

export const SORT_FIELDS = [
  'title',
  'status',
  'category',
  'due_date',
  'created_at',
  'patient_name',
] as const;

export type SortField = (typeof SORT_FIELDS)[number];

export type SortState = {
  field: SortField;
  order: 'asc' | 'desc';
} | null;

// --- request/response types moved by D1 (ARCFE-937) ------------------------

/**
 * Create-task request params — canonicalised here from THREE prior copies
 * (a private interface in `src/api/scribeAPIV2.ts` plus near-identical
 * exports in `useMutateCreate{,Session}ClinicalTask.ts`; the session variant
 * hardens `sessionId` to required at its call site). `sessionId` is optional
 * here because `createClinicalTask` accepts standalone tasks. The D2 hook
 * moves collapse the remaining copies onto this one.
 */
export interface CreateClinicalTaskParams {
  sessionId?: string;
  title: string;
  category?: ClinicalTaskCategory;
  description?: string;
  dueDate?: Date;
  assigneeId?: string | null;
  assigneeType?: AssigneeType;
  links?: ClinicalTaskLink[];
}

/** Bulk update/delete outcome — moved verbatim from the API hub (D1). */
export interface BulkClinicalTasksResponse {
  updated?: string[];
  deleted?: string[];
  failed: string[];
}

// --- moved verbatim from `packages/evidence/src/contracts/answers.ts` -------
// A clinical task is this domain's noun, so the row shape an Evidence answer
// block renders is declared here; `ClinicalTaskLink` above is the pick the
// Evidence copy re-declared privately.

// Model for clinical task in a result (EVI-490)
export interface ClinicalTaskResultTask {
  id: string;
  title: string;
  category: string | null;
  status: string;
  links?: ClinicalTaskLink[];
}
